/**
 * Browser detection utilities for handling browser-specific audio behaviors
 */

/**
 * Checks if the current browser is Safari (excluding Chrome-based browsers)
 *
 * Safari has the string "Safari" in its user agent, but Chrome-based browsers
 * (Chrome, Edge, Opera, etc.) also include "Safari" for compatibility.
 * We exclude Chrome-based browsers by checking for Chrome/Chromium/CriOS.
 */
export function isSafariBrowser(
  userAgent: string = navigator.userAgent
): boolean {
  return /Safari/i.test(userAgent) && !/Chrome|Chromium|CriOS/i.test(userAgent);
}

/**
 * Checks if the current device is an iOS device (iPhone, iPad, or iPod)
 */
export function isIOSDevice(userAgent: string = navigator.userAgent): boolean {
  return /iPhone|iPad|iPod/i.test(userAgent);
}

/**
 * Checks if the browser requires delayed audio element creation
 *
 * Both iOS Safari and desktop Safari have strict autoplay policies that require:
 * - Audio elements must be created during a user gesture (click/touch)
 * - The play() call must happen in the same call stack as the user gesture
 *
 * If audio elements are created outside of user gestures:
 * - iOS Safari: Blocks with "NotAllowedError"
 * - Desktop Safari 17.x: May block or mute audio playback
 *
 * This affects Safari on:
 * - All iOS devices (iPhone, iPad, iPod)
 * - Desktop Safari on macOS (especially versions 17.x and on older macOS 10)
 *
 * @returns true if the browser needs delayed audio creation, false otherwise
 */
export function needsDelayedAudioCreation(
  userAgent: string = navigator.userAgent
): boolean {
  // iOS Safari always needs delayed creation
  if (isIOSDevice(userAgent)) {
    return true;
  }

  // Desktop Safari also needs delayed creation due to autoplay restrictions
  // This is especially important for Safari 17.6 which has strict autoplay policies
  if (isSafariBrowser(userAgent) && !isIOSDevice(userAgent)) {
    return true;
  }

  return false;
}

/**
 * Checks if the current device is running macOS
 * Note: This will also return true for iOS devices in some cases,
 * so use in conjunction with isIOSDevice if you need to distinguish
 */
export function isMacOS(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check platform (most reliable for macOS)
  const platform = navigator.platform.toLowerCase();
  if (platform.includes('mac')) {
    return true;
  }

  // Check userAgent as fallback
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('macintosh') || userAgent.includes('mac os');
}

/**
 * Gets a descriptive label for the current browser/device combination
 * Useful for logging and debugging
 */
export function getBrowserLabel(
  userAgent: string = navigator.userAgent
): string {
  if (isIOSDevice(userAgent)) {
    return 'iOS Safari';
  }

  if (isSafariBrowser(userAgent)) {
    return 'Desktop Safari';
  }

  // Check Edge before Chrome since Edge also contains "Chrome" in its UA
  if (/Edge|Edg/i.test(userAgent)) {
    return 'Edge';
  }

  // Try to identify other common browsers
  if (/Chrome|Chromium/i.test(userAgent)) {
    return 'Chrome';
  }

  if (/Firefox/i.test(userAgent)) {
    return 'Firefox';
  }

  return 'Unknown Browser';
}
